/**
 * Довідник підказок (`Ship/Components/Hint`): де кожна стоїть — для списку
 * прибраних у профілі («Інтерфейс» → «Прибрані підказки»).
 *
 * На акаунті зберігаються лише ідентифікатори (`tutorial_hidden`), а
 * `crm.client.verification` людині нічого не каже. Тому кожен `id` має тут
 * розділ і місце — так, як їх видно в інтерфейсі. Текст підказки сюди не
 * копіюємо: він живе поруч зі своїм екраном і розійшовся б із копією.
 *
 * Вартовий `architectureGuard.test.ts` тримає довідник повним: нова `<Hint id>`
 * без запису тут — падіння; запис без підказки — теж.
 */
export interface HintCatalogEntry {
    /** Розділ меню або екран верхнього рівня. */
    section: string
    /** Місце на екрані — коротко, як його назве користувач. */
    place: string
}

export const HINT_CATALOG: Readonly<Record<string, HintCatalogEntry>> = {
    'dashboard.desktop.tiles': { section: 'Робочий стіл', place: 'Плитки справ' },
    'dashboard.transport.applications': { section: 'Робочий стіл', place: 'Заявки сьогодні' },

    'crm.activities.overdue': { section: 'Справи', place: 'Прострочені справи в списку' },
    'crm.activities.scope': { section: 'Справи', place: 'Чиї справи в списку' },
    'crm.activity.result': { section: 'Справи', place: 'Картка справи — результат' },

    'crm.client.responsibles': { section: 'Клієнти', place: 'Картка клієнта — відповідальні' },
    'crm.client.verification': { section: 'Клієнти', place: 'Картка клієнта — верифікація' },
    'crm.client.verify-contract': { section: 'Клієнти', place: 'Підтвердження верифікації — договір' },

    'crm.carrier.files': { section: 'Перевізники', place: 'Картка перевізника — файли' },
    'crm.carrier.responsible': { section: 'Перевізники', place: 'Картка перевізника — відповідальні' },
    'crm.carrier.verification': { section: 'Перевізники', place: 'Картка перевізника — верифікація' },
    'crm.carrier.verify-contract': { section: 'Перевізники', place: 'Підтвердження верифікації — договір' },

    'crm.documents.generation': { section: 'Друковані форми', place: 'Генерація документа' },
    'crm.documents.pdf': { section: 'Друковані форми', place: 'PDF документа' },
    'crm.letters.default-template': { section: 'Листи', place: 'Кнопка «Відправити лист»' },
    'crm.letters.mailbox': { section: 'Листи', place: 'З якої пошти йде лист' },

    'transport.application.status': { section: 'Заявки', place: 'Смуга статусів' },
    'transport.application.profit': { section: 'Заявки', place: 'Картка заявки — прибуток' },
    'transport.offers.counter': { section: 'Перевезення', place: 'Цінові пропозиції' },
    'transport.request.offered-price': { section: 'Запити й тендер', place: 'Запропонована ціна' },
    'transport.trip.scope': { section: 'Рейси', place: 'Які заявки в списку' },

    'report.financial.table': { section: 'Звіти', place: 'Звіт по логістам — фінансовий' },
    'report.involvement.table': { section: 'Звіти', place: 'Звіт по залученню логістів' },
    'report.losses.table': { section: 'Звіти', place: 'Звіт по програним заявкам' },

    'disk.company': { section: 'Диск', place: 'Простір «Компанія» — хто бачить і змінює' },
    'disk.quota': { section: 'Диск', place: 'Кошик — що займає квоту' },
    'disk.shared': { section: 'Диск', place: '«Спільні зі мною» — що тут і чого не видно' },
    'disk.trash': { section: 'Диск', place: 'Кошик — строк зберігання й відновлення' },
    'disk.admin.physical': { section: 'Адміністрування диска', place: 'Огляд — що звільняє квоту й місце на сервері' },
    'disk.admin.policy': { section: 'Адміністрування диска', place: 'Налаштування — кому дістаються нові строк кошика й версії' },
    'disk.admin.privacy': { section: 'Адміністрування диска', place: 'Диски — що видно на «Моєму диску» співробітника' },

    'team.departments.employees': { section: 'Структура компанії', place: 'Картка відділу — працівники' },
    'team.departments.move': { section: 'Структура компанії', place: 'Перенесення відділу' },
    'team.user.gmail': { section: 'Співробітники', place: 'Пароль Google Workspace' },
    'team.user.roles': { section: 'Співробітники', place: 'Права доступу' },
    'team.user.status': { section: 'Співробітники', place: 'Деактивація' },

    'settings.currency-rates.import': { section: 'Довідники', place: 'Курси валют — імпорт' },
    'settings.legal-entities.stamp': { section: 'Довідники', place: 'Юридичні особи — печатка' },
    'settings.lists.code': { section: 'Довідники', place: 'Код запису' },

    'shell.favorites.privacy': { section: 'Обране', place: 'Хто бачить обране' },
}

/**
 * Опис прибраної підказки для списку. Невідомий `id` (підказку прибрали з
 * коду, а на акаунті він лишився) показуємо як є — повернути його все одно
 * можна, і це прибере сміття з опції.
 */
export const describeHint = (id: string): HintCatalogEntry => HINT_CATALOG[id] ?? { section: 'Інше', place: id }
